import { CaptureContext } from '@sentry/types'
import z, { ZodError } from 'zod'

import {
  CSRF_TOKEN_EMPTY_EVENT_NAME,
  CSRF_TOKEN_EMPTY_FINGERPRINT,
} from 'utils/services/sentryConstantNames'

const UrlsWithoutCsrfToken = [
  /^\/analytics\/visit$/gi,
  /^\/auth\/(.)+$/gi,
  /^\/template\/[a-z0-9]+\/getData$/gi,
  /^\/businessEmail\/confirmBusinessEmail/gi,
  /^\/businessEmail\/getBusinessEmailInfo.*/gi,
  /^\/manychat\/getSharedFlow.*/gi,
  /^\/manychat\/previewState*/gi,
  /^\/appStore\/get.*/gi,
]

export const hasEventWithUrlWithoutCheckTokenOnBackend = ({
  exception,
  captureContext,
}: {
  exception?: unknown
  captureContext?: CaptureContext
}) => {
  const isObjectError = exception && typeof exception === 'object' && 'message' in exception
  const stringErrorMessage = isObjectError ? exception.message : exception

  const hasCsrfTitle = stringErrorMessage === CSRF_TOKEN_EMPTY_EVENT_NAME
  const hasCsrfFingerPrint =
    captureContext &&
    'fingerprint' in captureContext &&
    captureContext.fingerprint &&
    captureContext.fingerprint.includes(CSRF_TOKEN_EMPTY_FINGERPRINT)

  if (hasCsrfTitle || hasCsrfFingerPrint) {
    const hasExtra = captureContext && 'extra' in captureContext
    const url = hasExtra ? captureContext?.extra?.url ?? null : null
    return typeof url === 'string'
      ? UrlsWithoutCsrfToken.some((regex) => Boolean(url.match(regex)))
      : false
  }

  return false
}

interface ErrorTree {
  errors: string[]
  items?: ErrorTree[]
  properties?: Record<string, ErrorTree>
}

export const formatZodError = (error: ZodError) => {
  const errorTree: ErrorTree = z.treeifyError(error)
  const result: Record<string, string[]> = {}

  const mappingErrors = (structure: ErrorTree, propertyName: string = 'root') => {
    if (structure.errors.length) {
      structure.errors.forEach((error) => {
        if (!result[propertyName]) {
          result[propertyName] = [error]
          return
        }

        if (result[propertyName].includes(error)) {
          return
        }

        result[propertyName].push(error)
      })

      return
    }

    if (structure.items) {
      structure.items.forEach((item) => mappingErrors(item, `${propertyName}.[item]`))
    }

    if (structure.properties) {
      Object.entries(structure.properties).forEach(([key, value]) =>
        mappingErrors(value, `${propertyName}.${key}`),
      )
    }
  }

  mappingErrors(errorTree)

  return result
}
